import _ from 'lodash';
import type { RaterXPreset } from '../api/types/rater-x-preset';
import type { EditQuoteState } from '../views/edit-quote/types';
import type { UnderwriteQuoteState } from '../views/underwriter/types';
import type { CoverageState } from '../types';
import { flattenCoverages } from './coverages';
import type { PrimeXProductType } from '../../../../../types';
import type { Nullable } from '../../../../../types/common/utility';
import type { CustomizeQuoteState } from '../views/customize-quote/types';

/**
 * The key a multi-rater product files an item under: its own `key` and the part
 * it belongs to. One rule, read by the state transformers and by whatever has to
 * line a rater response up with that state (the draft's per-row premium).
 */
export const componentStateKey = (key: string, productType: string): string =>
  `${key}.${productType}`;

export const formatComponentCoveragesForState = (
  components: Nullable<RaterXPreset['components']>
) => {
  if (!components || _.isEmpty(components)) {
    return {};
  }

  const formattedCoverages: CoverageState = {};

  for (const [productType, component] of Object.entries(components || {})) {
    const flattenedCoverages = flattenCoverages(component.coverageCategories);

    for (const coverage of flattenedCoverages) {
      formattedCoverages[componentStateKey(coverage.key, productType)] = {
        ...coverage,
        productType: productType as PrimeXProductType,
      };
    }
  }

  return formattedCoverages;
};

const mergeComponentCoveragesIntoState = <
  T extends Partial<EditQuoteState & CustomizeQuoteState & UnderwriteQuoteState>
>(
  data: T
) => {
  if (!data.raterX?.components || _.isEmpty(data.raterX?.components)) {
    return data;
  }

  const coverageState = formatComponentCoveragesForState(
    data.raterX.components
  );

  return {
    ...data,
    coverageState: {
      ...data.coverageState,
      ...coverageState,
    },
  };
};

export default mergeComponentCoveragesIntoState;
